import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { handleToolError, maybeShow3DayOffer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatRupiah } from "@/lib/format";
import { Compass, ArrowRight, Loader2, Check } from "lucide-react";
import { ThreeDayOfferModal } from "@/components/ThreeDayOfferModal";
import { TrialLimitModal } from "@/components/TrialLimitModal";
import { OneAnalysisLeft } from "@/components/OneAnalysisLeft";

const QUESTIONS = [
  { key: "budget", label: "Anggaran Anda", options: [["<5jt", "< 5 juta"], ["5-10jt", "5–10 juta"], ["10-20jt", "10–20 juta"], [">20jt", "> 20 juta"]] },
  { key: "daily_km", label: "Jarak harian", options: [["<10", "< 10 km"], ["10-30", "10–30 km"], ["30-60", "30–60 km"], [">60", "> 60 km"]] },
  { key: "route", label: "Kondisi rute", options: [["datar", "Dominan datar"], ["campuran", "Sedikit tanjakan/flyover"], ["tanjakan", "Banyak tanjakan"]] },
  { key: "priority", label: "Prioritas utama", options: [["hemat", "Hemat"], ["jarak_jauh", "Jarak jauh"], ["tenaga", "Tenaga"], ["tanpa_sewa_baterai", "Tanpa sewa baterai"], ["servis_mudah", "Servis mudah"]] },
];

export default function GuidedChoice() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [ans, setAns] = useState({ budget: "5-10jt", daily_km: "10-30", route: "campuran", priority: "hemat" });
  const [items, setItems] = useState(null);
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(false);

  const pick = (k, v) => setAns({ ...ans, [k]: v });

  const submit = async () => {
    setLoading(true);
    api.post("/meta/track", { event: "guided_recommendation_used", meta: ans }).catch(() => {});
    try {
      const { data } = await api.post("/recommend", ans);
      setItems(data.items);
      setCredit(data.credit);
      maybeShow3DayOffer(data.credit);
      if (data.credit?.charged) checkAuth();
    } catch (err) {
      if (err.response?.status === 401) { navigate("/login"); }
      else handleToolError(err, navigate);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="guided-page">
      <ThreeDayOfferModal />
      <TrialLimitModal />
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy mb-2 flex items-center gap-3"><Compass className="w-8 h-8 text-teal" />Bantu Saya Memilih</h1>
      <p className="text-muted-foreground mb-8">Jawab 4 pertanyaan singkat, kami tampilkan 3 kandidat yang lebih sesuai dari database kami.</p>

      <div className="grid md:grid-cols-2 gap-5">
        {QUESTIONS.map((q) => (
          <Card key={q.key} className="rounded-2xl p-5" data-testid={`guided-q-${q.key}`}>
            <p className="font-semibold text-navy mb-3">{q.label}</p>
            <div className="flex flex-wrap gap-2">
              {q.options.map(([val, lbl]) => (
                <button key={val} data-testid={`guided-${q.key}-${val}`} onClick={() => pick(q.key, val)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${ans[q.key] === val ? "bg-teal text-white border-teal" : "bg-white text-navy border-border hover:border-teal"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Button data-testid="guided-submit" onClick={submit} disabled={loading} className="rounded-full bg-teal hover:bg-teal-hover h-13 py-6 px-8 mt-6 font-semibold">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Tampilkan Rekomendasi <ArrowRight className="w-5 h-5 ml-1" /></>}
      </Button>

      {items && (
        <div className="mt-10" data-testid="guided-results">
          <h2 className="font-heading font-bold text-2xl text-navy mb-1">3 Kandidat untuk Anda</h2>
          <p className="text-sm text-muted-foreground mb-5">Lebih sesuai berdasarkan kebutuhan yang Anda masukkan — bukan jaminan "terbaik".</p>
          {credit?.remaining === 1 && <div className="mb-5"><OneAnalysisLeft to="/hitung" label="Lanjutkan Analisis" /></div>}
          <div className="grid md:grid-cols-3 gap-5">
            {items.map((it, i) => {
              const v = it.vehicle;
              return (
                <Card key={v.vehicle_id} className={`rounded-2xl p-5 flex flex-col ${i === 0 ? "border-2 border-teal shadow-lg" : ""}`} data-testid={`guided-result-${i}`}>
                  {i === 0 && <span className="self-start text-xs font-bold text-teal bg-teal/10 px-3 py-1 rounded-full mb-2">Paling sesuai</span>}
                  <h3 className="font-heading font-bold text-navy">{v.brand} {v.model}</h3>
                  {v.variant && <p className="text-xs text-muted-foreground">{v.variant}</p>}
                  <p className="font-heading font-extrabold text-xl text-navy mt-2">{v.official_price ? formatRupiah(v.official_price) : "Harga belum tersedia"}</p>
                  <ul className="space-y-1.5 my-3 flex-1">
                    {it.reasons.map((r, j) => <li key={j} className="flex items-start gap-2 text-sm text-navy"><Check className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />{r}</li>)}
                  </ul>
                  <div className="flex flex-col gap-2">
                    <Button data-testid={`guided-detail-${i}`} onClick={() => navigate(`/kendaraan/${v.vehicle_id}`)} className="rounded-full bg-navy hover:bg-navy/90 h-10">Lihat Detail</Button>
                    <Button data-testid={`guided-calc-${i}`} variant="outline" onClick={() => navigate(`/hitung?ev=${v.vehicle_id}`)} className="rounded-full h-10">Hitung Hemat</Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
